import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Typography } from '@mui/material';

interface ConditionDonutProps {
  conditions: Record<string, number>;
  title?: string;
  height?: number;
}

const CONDITION_BANDS = [
  { name: 'Good', min: 60, max: 101, color: '#43a047' },
  { name: 'Poor', min: 40, max: 60, color: '#fb8c00' },
  { name: 'Critical', min: 20, max: 40, color: '#e53935' },
  { name: 'Failed', min: 0, max: 20, color: '#b71c1c' },
];

function buildConditionData(
  conditions: Record<string, number>,
): { name: string; y: number; color: string }[] {
  const counts = CONDITION_BANDS.map(b => ({ ...b, y: 0 }));

  for (const cond of Object.values(conditions)) {
    if (isNaN(cond)) continue;
    const band =
      counts.find(b => cond >= b.min && cond < b.max) ??
      counts[counts.length - 1];
    band.y += 1;
  }

  return counts
    .filter(c => c.y > 0)
    .map(({ name, y, color }) => ({ name, y, color }));
}

export default function ConditionDonut({
  conditions,
  title,
  height = 300,
}: ConditionDonutProps) {
  const options = useMemo<Highcharts.Options>(() => {
    const data = buildConditionData(conditions);

    return {
      chart: {
        type: 'pie',
        height,
        animation: { duration: 400 },
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      tooltip: {
        pointFormat: '<b>{point.y:,.0f}</b> groups ({point.percentage:.1f}%)',
      },
      plotOptions: {
        pie: {
          innerSize: '50%',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>{point.percentage:.1f}%',
            connectorWidth: 1,
            distance: 12,
            style: { fontWeight: '400', textOutline: 'none', fontSize: '11px' },
          },
        },
      },
      series: [{ type: 'pie' as const, name: 'Groups', data }],
      credits: { enabled: false },
    };
  }, [conditions, height]);

  if (!conditions || Object.keys(conditions).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No condition data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 0.5, textAlign: 'center' }}>
          {title}
        </Typography>
      )}
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        updateArgs={[true, true, true]}
      />
    </Box>
  );
}
